import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import { flattenCssVariables } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/flattenCssVariables";
import { resolveCssVariable } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/resolveCssVariable";
import {
  type StyleDefinition,
  styleMap,
} from "@circle-react-shared/BuilderUI/utils/styleMap";

export const resolveVariableValuesFromStyleObject = ({
  styleObject,
  currentTheme,
}: {
  styleObject: object | null;
  currentTheme: SiteTheme;
}) =>
  Object.entries(styleObject ?? {}).reduce(
    (acc, [key, value]) => {
      acc[key] = resolveCssVariable({
        value,
        cssVariables: flattenCssVariables(currentTheme.cssVariables, "light"),
      });
      return acc;
    },
    {} as Record<string, string | number>,
  );

export const getVisualEmailBuilderStyleMap = (
  theme: SiteTheme,
): Record<string, StyleDefinition> => ({
  ...styleMap,
  fontSize: {
    cssProperty: "fontSize",
    processorType: "unit",
    defaultUnit: "px",
  },
  fontWeight: {
    cssProperty: "fontWeight",
    processorType: "custom",
    processor: value => {
      const val = parseInt(String(value.value), 10);
      return isNaN(val)
        ? resolveCssVariable({
            value: String(value.value),
            cssVariables: flattenCssVariables(theme.cssVariables, "light"),
          })
        : val;
    },
  },
  borderRadius: {
    cssProperty: "borderRadius",
    processorType: "custom",
    processor: value => {
      if (typeof value.value === "object") {
        const corners = resolveVariableValuesFromStyleObject({
          styleObject: value.value,
          currentTheme: theme,
        }) as {
          border_left_top_radius: number | string;
          border_right_top_radius: number | string;
          border_right_bottom_radius: number | string;
          border_left_bottom_radius: number | string;
        };

        return `${corners.border_left_top_radius}px ${corners.border_right_top_radius}px ${corners.border_right_bottom_radius}px ${corners.border_left_bottom_radius}px`;
      }
      return typeof value.value === "number" ? `${value.value}px` : undefined;
    },
  },
  padding: {
    cssProperty: "padding",
    processorType: "custom",
    processor: value => {
      const val = value.value as {
        paddingTop?: number | string;
        paddingBottom?: number | string;
        paddingLeft?: number | string;
        paddingRight?: number | string;
      };

      if (typeof value.value === "object") {
        const { paddingTop, paddingBottom, paddingLeft, paddingRight } =
          resolveVariableValuesFromStyleObject({
            styleObject: val,
            currentTheme: theme,
          });

        return `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`;
      }

      return undefined;
    },
  },
  margin: {
    cssProperty: "margin",
    processorType: "custom",
    processor: value => {
      const val = value.value as {
        marginTop?: number | string;
        marginBottom?: number | string;
        marginLeft?: number | string;
        marginRight?: number | string;
      };

      if (typeof value.value === "object") {
        const { marginTop, marginBottom, marginLeft, marginRight } =
          resolveVariableValuesFromStyleObject({
            styleObject: val,
            currentTheme: theme,
          });
        return `${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px`;
      }

      return undefined;
    },
  },
  opacity: {
    cssProperty: "opacity",
    processorType: "unit",
    defaultUnit: "",
  },
});
